import React from "react";
import {ApiProvider, BaseComponent, ch, IResponseContainer, PageRouteProvider} from "@renta-apps/athenaeum-react-common";
import {AddressHelper, Layout} from "@renta-apps/athenaeum-react-components";
import UserInteractionDataStorage from "@/providers/UserInteractionDataStorage";
import RentaTasksController from "@/pages/RentaTasks/RentaTasksController";
import PageDefinitions from "@/providers/PageDefinitions";
import FlagProvider from "@unleash/proxy-client-react";
import UnleashHelper from "@/helpers/UnleashHelper";
import logo from './renta-logo.png';
import GetInfoBarResponse from "@/models/server/responses/GetInfoBarResponse";
import ViewPreferenceStorage from "@/providers/ViewPreferenceStorage";
import UserContext from "@/models/server/UserContext";

interface IAppState {
    showInfoBar: boolean,
    infoBarText: "",
}

class App extends BaseComponent {
    state: IAppState = {
        showInfoBar: false,
        infoBarText: "",
    }
    
    public async componentDidMount(): Promise<void> {
        await super.componentDidMount();
        await this.refreshInfoBarAsync();
    }

    private async refreshInfoBarAsync(): Promise<void> {
        try {
            const response: GetInfoBarResponse = await this.getInfoBarAsync();
            this.setState({
                showInfoBar: response.enabled,
                infoBarText: response.text,
            });
        } catch (e) {
            // Ignore error
        }
    }
    
    private async getInfoBarAsync(): Promise<GetInfoBarResponse> {
        const response = await fetch("api/application/getInfoBar", { method: "GET" });
        const result = JSON.parse(await response.text()) as IResponseContainer;
        return result.value as GetInfoBarResponse;
    }

    render(): React.ReactNode {
        return (
            <FlagProvider unleashClient={UnleashHelper.client} startClient={false}>
                <Layout christmasLightsEnabled={false}
                        onLogoClick={RentaTasksController.onLogoClickAsync}
                        topNavLogo={logo}
                        footerLogo={logo}
                        tokenLogin={async (_, token) => {await this.tokenLoginAsync(token)}}
                        showTopNavInfo={this.state.showInfoBar}
                        topNavInfoText={this.state.infoBarText}
                        onDidMount={async () => await App.handleDeepLinkAsync()}
                />
            </FlagProvider>
        );
    }

    public async tokenLoginAsync(token: string): Promise<any> {
        await ApiProvider.invokeWithForcedSpinnerAsync(
            () => ApiProvider.postAsync("api/Application/TokenLogin", token));

        // Reload with an empty path to get rid of ?token= from the URL bar.
        const path: string | null = null;
        await PageRouteProvider.changeUrlWithoutReload(path);
    }

    /**
     * Deep-link handler: redirects to a work order if a pending deep-link exists and the user is authenticated.
     */
    public static async handleDeepLinkAsync(): Promise<void> {
        const workOrderId = sessionStorage.getItem("pendingDeepLinkWorkOrderId");
        const workOrderRouteType = sessionStorage.getItem("pendingDeepLinkWorkOrderRouteType");
        if (workOrderId && ch.isAuthenticated) {
            sessionStorage.removeItem("pendingDeepLinkWorkOrderId");
            sessionStorage.removeItem("pendingDeepLinkWorkOrderRouteType");
            const route = workOrderRouteType === "rentaTasks"
                ? PageDefinitions.rentaTasksWorkOrder(workOrderId)
                : PageDefinitions.workOrderRoute(workOrderId);
            await PageRouteProvider.redirectAsync(route);
        }
    }
}

//Deep-link: capture work order URL before any redirects occur, then reset path so relative API calls resolve correctly
const deepLinkRentaTasksMatch = window.location.pathname.match(/^\/RentaTasks\/WorkOrder\/([0-9a-fA-F-]{36})$/i);
const deepLinkWorkOrderMatch = window.location.pathname.match(/^\/WorkOrder\/([0-9a-fA-F-]{36})$/i);
if (deepLinkRentaTasksMatch) {
    sessionStorage.setItem("pendingDeepLinkWorkOrderId", deepLinkRentaTasksMatch[1]);
    sessionStorage.setItem("pendingDeepLinkWorkOrderRouteType", "rentaTasks");
    window.history.replaceState(null, "", "/");
} else if (deepLinkWorkOrderMatch) {
    sessionStorage.setItem("pendingDeepLinkWorkOrderId", deepLinkWorkOrderMatch[1]);
    sessionStorage.setItem("pendingDeepLinkWorkOrderRouteType", "workOrder");
    window.history.replaceState(null, "", "/");
}

//Register Unleash
ch.registerInitializeCallback(async () => await UnleashHelper.initialize());
//Register initialize events
ch.registerInitializeCallback(async () => await AddressHelper.loadGoogleLibraryAsync());
ch.registerInitializeCallback(async () => await RentaTasksController.initializeAsync());
//Register authorize events
ch.registerAuthorizeCallback(async () => await UserInteractionDataStorage.onAuthorize());
ch.registerAuthorizeCallback(async () => await RentaTasksController.authorizeAsync());
//View preference: sync localStorage preference to server session and override initial redirect
ch.registerAuthorizeCallback(async () => {
    if (!ch.isAuthenticated) {
        return;
    }

    const userContext = ch.getContext() as UserContext;

    if (userContext.isMounter || userContext.isSubcontractorMounter || userContext.isMobileManager) {
        return;
    }

    let preference = ViewPreferenceStorage.get(userContext.email);
    if (preference === null) {
        preference = ViewPreferenceStorage.get();
    }

    if (preference === null) {
        return;
    }

    ViewPreferenceStorage.set(preference, userContext.email);

    await ApiProvider.postAsync("/api/Application/SetViewPreference", preference);

    const targetRoute = preference
        ? PageDefinitions.rentaTasksRoute
        : PageDefinitions.dashboardRoute;

    const provider = PageRouteProvider as any;
    const originalRedirect = provider.redirectAsync;
    provider.redirectAsync = async function(route: any, replace?: boolean, stopPropagation?: boolean) {
        provider.redirectAsync = originalRedirect;
        return originalRedirect.call(provider, targetRoute, replace, stopPropagation);
    };
});
//Deep-link: after login, intercept the framework's next redirect to go directly to WorkOrder
//instead of Dashboard, preventing partial page initialization errors.
ch.registerAuthorizeCallback(async () => {
    const workOrderId = sessionStorage.getItem("pendingDeepLinkWorkOrderId");
    const workOrderRouteType = sessionStorage.getItem("pendingDeepLinkWorkOrderRouteType");
    if (workOrderId) {
        sessionStorage.removeItem("pendingDeepLinkWorkOrderId");
        sessionStorage.removeItem("pendingDeepLinkWorkOrderRouteType");
        const deepLinkRoute = workOrderRouteType === "rentaTasks"
            ? PageDefinitions.rentaTasksWorkOrder(workOrderId)
            : PageDefinitions.workOrderRoute(workOrderId);
        const provider = PageRouteProvider as any;
        const originalRedirect = provider.redirectAsync;
        provider.redirectAsync = async function(route: any, replace?: boolean, stopPropagation?: boolean) {
            provider.redirectAsync = originalRedirect;
            return originalRedirect.call(provider, deepLinkRoute, replace, stopPropagation);
        };
    }
});

export default App;